import { Group, Stack } from "@mantine/core"
import { getCoreRowModel, useReactTable } from "@tanstack/react-table"
import { ExportButton, ExportType, Select, DateInput } from "@tfg/ui"
import { Dispatch, SetStateAction, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import z from "zod"

import { AdminResponses } from "@tfg/types/endpoints/admin"
import { findBouncedEmailsFilterType } from "@tfg/types/schemas/admin"
import { UndeliveredEventsSchema } from "@tfg/types/schemas/enum"

import { Table } from "~/components/table/Table"
import { useGetExportColumns } from "~/pages/Admin/Exports/BouncedEmails/export-columns"
import { useExportBouncedEmails } from "~/pages/Admin/Exports/BouncedEmails/hooks/useExportBouncedEmails"

type Props = {
  bouncedEmails: AdminResponses.FindAllBouncedEmailsResponseDto
  filters: findBouncedEmailsFilterType
  setFilters: Dispatch<SetStateAction<findBouncedEmailsFilterType>>
  setPagination: Dispatch<
    SetStateAction<{ pageIndex: number; pageSize: number }>
  >
  pagination: { pageIndex: number; pageSize: number }
}

export const BouncedEmailsTable = ({
  bouncedEmails,
  filters,
  setFilters,
  setPagination,
  pagination,
}: Props) => {
  const columns = useGetExportColumns()
  const { mutate: csvDownload, isLoading: isExportingBouncedEmails } =
    useExportBouncedEmails({
      filters,
    })
  const { t } = useTranslation(["products", "common"])
  const [pageCount, setPageCount] = useState<number>(
    Math.ceil(bouncedEmails.total / pagination.pageSize)
  )

  useEffect(() => {
    setPageCount(Math.ceil(bouncedEmails.total / pagination.pageSize))
  }, [pagination.pageSize, bouncedEmails.total])

  const table = useReactTable({
    data: bouncedEmails.bouncedEmails,
    columns,
    state: {
      pagination,
    },
    pageCount,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  })

  return (
    <Stack>
      <Group justify="space-between">
        <Group>
          <Select
            data={bouncedEmails.events}
            label="Event"
            placeholder="Select event"
            searchable
            clearable
            size="xs"
            value={filters.event}
            onChange={(event) => {
              const eventType = event as z.infer<typeof UndeliveredEventsSchema>
              setFilters({ ...filters, event: eventType })
            }}
          />
          <Select
            data={bouncedEmails.companies.map((company) => ({
              value: company.id,
              label: company.name,
            }))}
            label="Inviter Company"
            placeholder="Select event"
            searchable
            clearable
            value={filters.inviterCompanyId}
            size="xs"
            onChange={(inviterCompanyId) => {
              setFilters({ ...filters, inviterCompanyId })
            }}
          />
          <Select
            data={bouncedEmails.suppliers}
            label="Supplier Email"
            placeholder="Select event"
            searchable
            clearable
            size="xs"
            value={filters.supplierEmail}
            onChange={(supplierEmail) =>
              setFilters({ ...filters, supplierEmail })
            }
          />
          <DateInput
            miw={150}
            clearable
            size="xs"
            label="From date"
            value={filters.date}
            placeholder="Select date"
            onChange={(date) => {
              setFilters({ ...filters, date })
            }}
          />
        </Group>
        <Group>
          <ExportButton
            label={t("lists.export.all")}
            exportOptions={[
              {
                type: ExportType.CSV,
                label: t("common:actionButtons.exportAsCsv"),
                exportFunction: csvDownload,
                isLoading: isExportingBouncedEmails,
              },
            ]}
          />
        </Group>
      </Group>
      <Table
        table={table}
        pageIndex={pagination.pageIndex}
        manualPaginationCount={bouncedEmails.total || 0}
      />
    </Stack>
  )
}
